import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api, type ReceiptHistoryItem } from "../lib/api";
import { AppError, logError, toUserMessage } from "../lib/errors";

function formatReceiptDate(value: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", { month: "numeric", day: "numeric" }).format(d);
}

function formatProcessedAt(value: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function formatAmount(amount: string | null, currency: string | null) {
  if (!amount) return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  const sym = currency === "JPY" || !currency ? "¥" : currency;
  return `${sym}${n.toLocaleString("ja-JP")}`;
}

type StatusBadge = { label: string; className: string };

function getStatusBadge(item: ReceiptHistoryItem): StatusBadge {
  if (item.relayStatus === "accepted") {
    return { label: "処理中", className: "bg-[#F0ECE8] text-[#7C6A60]" };
  }
  if (item.relayStatus === "failed") {
    return { label: "エラー", className: "bg-[#FEF0EE] text-[#B4533A]" };
  }
  if (item.needsReview) {
    return { label: "要確認", className: "bg-[#FEF3E8] text-[#B07030]" };
  }
  return { label: "確認済", className: "bg-[#EEF6EC] text-[#4A7C59]" };
}

function ReceiptCard({ item }: { item: ReceiptHistoryItem }) {
  const badge = getStatusBadge(item);
  const amount = formatAmount(item.totalAmount, item.currency);
  const dateDisplay = formatReceiptDate(item.receiptDate) ?? formatProcessedAt(item.processedAt);

  return (
    <article className="overflow-hidden rounded-2xl border border-[#EADBC8] bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-black ${badge.className}`}>
              {badge.label}
            </span>
            {dateDisplay && (
              <span className="text-xs text-[#9C867D]">{dateDisplay}</span>
            )}
          </div>
          <p className="mt-1 truncate text-sm font-black text-[#5D4D46]">
            {item.merchantName ?? "（店舗名不明）"}
          </p>
          {item.relayStatus === "accepted" && (
            <p className="mt-0.5 text-xs text-[#9C867D]">OCR処理中...</p>
          )}
          {item.relayStatus === "failed" && (
            <p className="mt-0.5 text-xs text-[#B4533A]">処理に失敗しました</p>
          )}
          {amount && (
            <p className="mt-1 text-base font-black text-[#5D4D46]">{amount}</p>
          )}
        </div>
        <Link
          to={`/receipt/${item.jobId}`}
          className="min-h-[42px] w-full shrink-0 rounded-full bg-[#7C5A4E] px-4 py-2 text-center text-sm font-black text-white shadow-sm transition hover:bg-[#5D4D46] sm:w-auto"
        >
          詳細を見る
        </Link>
      </div>
    </article>
  );
}

type FilterValues = {
  dateFrom: string;
  dateTo: string;
  amountMin: string;
  amountMax: string;
  needsReview: "" | "true" | "false";
};

const EMPTY_FILTER: FilterValues = { dateFrom: "", dateTo: "", amountMin: "", amountMax: "", needsReview: "" };

function hasFilter(f: FilterValues) {
  return f.dateFrom !== "" || f.dateTo !== "" || f.amountMin !== "" || f.amountMax !== "" || f.needsReview !== "";
}

function FilterPanel({
  onApply,
  onReset,
  loading,
}: {
  onApply: (f: FilterValues) => void;
  onReset: () => void;
  loading: boolean;
}) {
  const [draft, setDraft] = useState<FilterValues>(EMPTY_FILTER);
  const applied = useRef<FilterValues>(EMPTY_FILTER);

  const set = (key: keyof FilterValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((prev) => ({ ...prev, [key]: e.target.value }));

  const setNeedsReview = (value: FilterValues["needsReview"]) =>
    setDraft((prev) => ({ ...prev, needsReview: value }));

  const handleApply = () => {
    applied.current = draft;
    onApply(draft);
  };

  const handleReset = () => {
    setDraft(EMPTY_FILTER);
    applied.current = EMPTY_FILTER;
    onReset();
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(applied.current);

  return (
    <div className="mt-4 rounded-2xl border border-[#E8D5C8] bg-[#FFFAF7] p-4">
      <p className="mb-3 text-xs font-black tracking-wide text-[#A67C52]">絞り込み</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">領収書日付（開始）</label>
          <input
            type="date"
            value={draft.dateFrom}
            onChange={set("dateFrom")}
            className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">領収書日付（終了）</label>
          <input
            type="date"
            value={draft.dateTo}
            onChange={set("dateTo")}
            className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">金額（最小）</label>
          <input
            type="number"
            min="0"
            value={draft.amountMin}
            onChange={set("amountMin")}
            placeholder="例: 1000"
            className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">金額（最大）</label>
          <input
            type="number"
            min="0"
            value={draft.amountMax}
            onChange={set("amountMax")}
            placeholder="例: 50000"
            className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
          />
        </div>
      </div>
      <div className="mt-3">
        <p className="mb-2 text-xs font-bold text-[#7C5A4E]">ステータス</p>
        <div className="flex flex-wrap gap-4">
          {([["", "すべて"], ["true", "要確認"], ["false", "確認済"]] as const).map(([val, label]) => (
            <label key={val} className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-bold text-[#5D4D46]">
              <input
                type="radio"
                name="needsReview"
                value={val}
                checked={draft.needsReview === val}
                onChange={() => setNeedsReview(val)}
                className="accent-[#A67C52]"
              />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={handleApply}
          disabled={loading || !dirty}
          className="flex-1 rounded-full bg-[#A67C52] py-2 text-sm font-black text-white hover:bg-[#7C5A4E] disabled:opacity-50"
        >
          絞り込む
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={loading}
          className="rounded-full border border-[#A67C52] px-4 py-2 text-sm font-black text-[#A67C52] hover:bg-[#F5EDE8] disabled:opacity-50"
        >
          リセット
        </button>
      </div>
    </div>
  );
}

export default function ReceiptHistoryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ReceiptHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterValues>(EMPTY_FILTER);

  const loadPage = useCallback(
    async (page: number, append: boolean, filter: FilterValues) => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      setErrorMessage(null);

      try {
        const params: Parameters<typeof api.getReceiptHistory>[0] = { page, perPage: 20 };
        if (filter.dateFrom) params.dateFrom = filter.dateFrom;
        if (filter.dateTo) params.dateTo = filter.dateTo;
        if (filter.amountMin !== "") params.amountMin = Number(filter.amountMin);
        if (filter.amountMax !== "") params.amountMax = Number(filter.amountMax);
        if (filter.needsReview !== "") params.needsReview = filter.needsReview === "true";

        const list = await api.getReceiptHistory(params);
        setItems((prev) => (append ? [...prev, ...list.data] : list.data));
        setCurrentPage(list.meta.currentPage);
        setLastPage(list.meta.lastPage);
        setTotal(list.meta.total);
      } catch (error) {
        if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
          navigate("/splash", { replace: true });
          return;
        }
        logError(error);
        setErrorMessage(toUserMessage(error));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    void loadPage(1, false, EMPTY_FILTER);
  }, [loadPage]);

  const handleApplyFilter = (f: FilterValues) => {
    setActiveFilter(f);
    void loadPage(1, false, f);
  };

  const handleResetFilter = () => {
    setActiveFilter(EMPTY_FILTER);
    void loadPage(1, false, EMPTY_FILTER);
  };

  const hasMore = currentPage < lastPage;
  const isFiltered = hasFilter(activeFilter);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/receipt" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← 戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">領収書の記録</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">領収書整理</p>
              <h1 className="mt-3 text-2xl font-black tracking-tight text-[#7C5A4E]">領収書の記録</h1>
            </div>
            <Link
              to="/receipt"
              className="inline-flex min-h-[40px] items-center justify-center rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46]"
            >
              新しい領収書を追加
            </Link>
          </div>

          <FilterPanel
            onApply={handleApplyFilter}
            onReset={handleResetFilter}
            loading={loading || loadingMore}
          />

          {!loading && errorMessage && (
            <p className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">{errorMessage}</p>
          )}
        </section>

        <section
          aria-label="領収書の記録一覧"
          className="mt-5 rounded-[2rem] border border-[#EADBC8] bg-white/80 p-4 shadow-[0_18px_48px_rgba(124,90,78,0.12)] sm:p-5"
        >
          {!loading && total !== null && (
            <p className="mb-3 text-xs text-[#9C867D]">
              {isFiltered ? `絞り込み結果：${total}件` : `全${total}件`}
            </p>
          )}

          {loading ? (
            <p className="py-10 text-center text-sm text-[#9C867D]">読み込み中...</p>
          ) : !errorMessage && items.length === 0 ? (
            <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-[#EADBC8] bg-[#FFFCFA] p-5 text-center text-sm leading-7 text-[#6B5851]">
              {isFiltered ? "条件に一致する領収書がありません。" : "領収書の記録がありません。"}
            </div>
          ) : (
            <div className="grid gap-3">
              {items.map((item) => (
                <ReceiptCard key={item.jobId} item={item} />
              ))}
            </div>
          )}

          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => void loadPage(currentPage + 1, true, activeFilter)}
                disabled={loadingMore}
                className="rounded-full border border-[#D8C0AF] bg-white px-6 py-2.5 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:opacity-50"
              >
                {loadingMore ? "読み込み中..." : "もっと見る"}
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
